import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Location } from '@angular/common';
import { UntypedFormControl, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';

import { InspectionInfoPluginService } from './inspectionInfo-plugin.service';
import { BaseModalPageComponent } from '../../base-modal.component';
import { ModalComponent } from '../../modal/modal.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { Broadcaster } from '../../utils/index';
import * as _ from 'lodash';
import { InspectionService } from '../../shared/services';
import { of, zip } from 'rxjs';

@Component({
    selector: 'inspectionInfo-edit',
    templateUrl: './inspectionInfo-edit.component.html',
    standalone: false
})
export class InspectionInfoEditPageComponent extends BaseModalPageComponent implements OnInit {
  inspectionId: string;
  pluginId: string;
  public plugin: any;
  editForm: UntypedFormGroup;
  public processing = false;

  @ViewChild('menuModal', { static: true })
  menuModal: ModalComponent;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('TypeInspectionsModal', { static: true })
  TypeInspectionsModal: ModalComponent;
  @ViewChild('statusUpdateConfirm', { static: true }) statusUpdateConfirm: ConfirmComponent;
  public types: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private service: InspectionInfoPluginService,
    private inspectionService: InspectionService,
    location: Location,
    private cdRef: ChangeDetectorRef,
    private broadcaster: Broadcaster
  ) {
    super(location);
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.goToBack();

      this.inspectionId = id;

      const pluginId = params['pluginId'];
      if (pluginId == null) this.goToBack();

      this.pluginId = pluginId;

      this.initData();
    });
  }

  updateStatus() {
    this.inspectionService.updateStatus(this.inspectionId, 150).subscribe(() => {
      this.processing = false;
      this.goToBack();
    });
  }

  save(model: any) {
    this.processing = true;
    const updateStatus =
      !!model.StartDate &&
      this.plugin.inspection.status === 0 &&
      !this.plugin.inspection.isOffline &&
      this.isConnection;

    this.service.updateInspectionInfo(this.inspectionId, this.pluginId, model).subscribe(() => {
      if (!updateStatus) {
        this.processing = false;
        this.goToBack();
        return;
      }

      this.statusUpdateConfirm.open();
    });
  }

  cancel() {
    this.goToBack();
  }

  ngOnInit() {
    this.initRoute();
  }

  initData() {
    this.types = _.orderBy(this.service.getTypes(this.inspectionId), ['label']);
    this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((data) => {
      this.plugin = data;
      this.initForm();
      this.cdRef.detectChanges();
    });
  }

  initForm() {
    this.editForm = new UntypedFormGroup({
      StartDate: new UntypedFormControl(this.plugin.config.StartDate),
      EndDate: new UntypedFormControl(this.plugin.config.EndDate),
      DefinedDuration: new UntypedFormControl(this.plugin.config.DefinedDuration),
      NotificationNumber: new UntypedFormControl(this.plugin.config.NotificationNumber),
      ServiceOrder: new UntypedFormControl(this.plugin.config.ServiceOrder),
      WorkOrders: new UntypedFormArray([]),
      TypeInspection: new UntypedFormControl(this.plugin.config.TypeInspection)
    });

    const initialValues = this.getInitialWorkOrders();
    initialValues.forEach((v) => this.WorkOrders.push(new UntypedFormControl(v, [Validators.required, Validators.maxLength(8), Validators.pattern(/^\d{0,8}$/)])));
  }

  private getInitialWorkOrders(): string[] {
    const workOrders = this.plugin.config.WorkOrders;
    if (workOrders && workOrders.length > 0) {
      return workOrders;
    }
    if (this.plugin.config.ServiceOrder) {
      return [this.plugin.config.ServiceOrder];
    }
    return [''];
  }

  get WorkOrders(): UntypedFormArray {
    return this.editForm.get('WorkOrders') as UntypedFormArray;
  }

  addWorkOrder() {
    if (this.WorkOrders.length < 5) {
      this.WorkOrders.push(new UntypedFormControl('', [Validators.required, Validators.maxLength(8), Validators.pattern(/^\d{0,8}$/)]));
    }
  }

  removeWorkOrder(i: number) {
    if (this.WorkOrders.length > 1) {
      this.WorkOrders.removeAt(i);
    }
  }

  public selectTypeInspectionsMenu(item: any) {
    const selectedIndex =
      this.editForm.controls['TypeInspection'].value === item.selectedIndex ? -1 : item.selectedIndex;
    this.editForm.controls['TypeInspection'].setValue(selectedIndex);
  }

  public onTypeInspectionsMenuClose(e: any) {
    this.editForm.controls['TypeInspection'].markAsTouched();
  }

  public openTypeInspectionsSelector(): void {
    this.openSelector(this.TypeInspectionsModal, this.types, this.editForm.controls['TypeInspection'].value);
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.openDeleteConfirm();
        break;
      case 10:
        this.gotoEditJsonPage();
        break;
      case 11:
        this.gotoEditPage();
        break;
      default:
    }
  }

  gotoEditJsonPage() {
    return this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      -1,
      'plugins',
      this.pluginId,
      'editjson'
    ]);
  }

  gotoEditPage() {
    return this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      -1,
      'plugins',
      this.pluginId,
      'edit'
    ]);
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deletePlugin(e: any) {
    this.service.deletePlugin(this.inspectionId, null, this.pluginId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }
}
